import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { useTranslation } from 'react-i18next';
import { useControl } from 'react-map-gl';

import styles from './geocoder-control.module.css';

/* eslint-disable-next-line */
export interface GeocoderControlProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

interface CoordinateFeatureGeometry {
  type: "Point";
  coordinates: [number, number];
}

interface CoordinateFeature {
  center: [number, number];
  geometry: CoordinateFeatureGeometry;
  place_name: string;
  place_type: ['coordinate'];
  properties: Record<string, unknown>;
  type: "Feature";
  bbox: [number, number, number, number];
  relevance: number;
  text: string;
  address: string;
  context: unknown[];
}

type CoordinatesGeocoderResult = CoordinateFeature[];

const coordinatesGeocoder = (query: string): CoordinatesGeocoderResult => {
  // Match anything which looks like a decimal degrees coordinate pair.
  const matches = query.match(/^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i);
  if (!matches) {
    return []; // Return an empty array instead of null
  }
  function coordinateFeature(lng: number, lat: number): CoordinateFeature {
    return {
      center: [lng, lat],
      geometry: {
        type: "Point",
        coordinates: [lng, lat],
      },
      place_name: 'Lat: ' + lat + ' Lng: ' + lng,
      place_type: ['coordinate'],
      properties: {},
      type: "Feature",
      bbox: [lng, lat, lng, lat],
      relevance: 1,
      text: `Lat: ${lat} Lng: ${lng}`,
      address: '',
      context: [],
    };
  }

  const coord1 = Number(matches[1]);
  const coord2 = Number(matches[2]);
  const geocodes: CoordinateFeature[] = [];

  if (coord1 < -90 || coord1 > 90) {
    // must be lng, lat
    geocodes.push(coordinateFeature(coord1, coord2));
  }

  if (coord2 < -90 || coord2 > 90) {
    // must be lat, lng
    geocodes.push(coordinateFeature(coord2, coord1));
  }

  if (geocodes.length === 0) {
    // else could be either lng, lat or lat, lng
    geocodes.push(coordinateFeature(coord1, coord2));
    geocodes.push(coordinateFeature(coord2, coord1));
  }

  return geocodes;
};

export function GeocoderControl(props: GeocoderControlProps) {
  const { i18n } = useTranslation();
  useControl(
    () => {
      const geocoder = new MapboxGeocoder({
        accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
        marker: false,
        language: i18n.language,
        localGeocoder: coordinatesGeocoder,
        zoom: 4,
        placeholder: 'Coordonnées ou adresse',
        ...props,
      });

      return geocoder;
    },
    {
      position: props.position,
    }
  );

  return null;
}

export default GeocoderControl;
